import React, { useEffect, useRef, useState } from "react";

interface MeditationHistogramPlayerProps {
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    title: string | null;
    onPlay: () => void;
    onPause: () => void;
    audioRef: React.RefObject<HTMLAudioElement>;
}

// AIDEV-NOTE: Colors from particle animation - cycles through blue, purple, rose, teal
const GRADIENT_COLORS = [
    { r: 59, g: 130, b: 246 }, // Blue
    { r: 147, g: 51, b: 234 }, // Purple
    { r: 244, g: 63, b: 135 }, // Rose
    { r: 20, g: 184, b: 166 }, // Teal
];

// AIDEV-NOTE: Histogram visualization with animated bars - NOT time-based
export function MeditationHistogramPlayer({
    isPlaying,
    currentTime,
    duration,
    title,
    onPlay,
    onPause,
    audioRef,
}: MeditationHistogramPlayerProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationFrameRef = useRef<number>();
    const gradientPositionRef = useRef(0);
    const lastUpdateRef = useRef<number>(0);
    const [barHeights, setBarHeights] = useState<number[]>(() => {
        // AIDEV-NOTE: Initialize 60 bars at very low height (almost a line)
        return new Array(60).fill(0.05);
    });

    // AIDEV-NOTE: Format time as MM:SS
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    // AIDEV-NOTE: Get color from gradient position (0-1) across all gradient colors
    const getColorFromGradient = (
        position: number,
        opacity: number,
    ): string => {
        const normalizedPos = position % 1;
        const colorCount = GRADIENT_COLORS.length;
        const segment = normalizedPos * colorCount;
        const index = Math.floor(segment);
        const t = segment - index;

        const color1 = GRADIENT_COLORS[index % colorCount];
        const color2 = GRADIENT_COLORS[(index + 1) % colorCount];

        const r = Math.round(color1.r + (color2.r - color1.r) * t);
        const g = Math.round(color1.g + (color2.g - color1.g) * t);
        const b = Math.round(color1.b + (color2.b - color1.b) * t);

        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };

    // AIDEV-NOTE: Web Audio API setup removed - using simulated animation instead
    // AIDEV-NOTE: Web Audio API was causing CORS issues and breaking audio playback

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // AIDEV-NOTE: Set canvas size to match container
        const resizeCanvas = () => {
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width * window.devicePixelRatio;
            canvas.height = rect.height * window.devicePixelRatio;
            ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        };

        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);

        const animate = () => {
            if (!canvas) return;

            const width = canvas.width / window.devicePixelRatio;
            const height = canvas.height / window.devicePixelRatio;

            // AIDEV-NOTE: Throttle bar height updates to prevent seizure-inducing rapid flashing
            const now = Date.now();
            const shouldUpdate = now - lastUpdateRef.current > 50; // Update every 50ms (faster)

            if (shouldUpdate) {
                lastUpdateRef.current = now;

                // AIDEV-NOTE: Simulate gentle audio-reactive animation
                // AIDEV-NOTE: Uses slow, calm breathing-like rhythm with "mouth opening" effect
                if (isPlaying) {
                    // AIDEV-NOTE: Faster sine wave for more dynamic breathing rhythm
                    const time = now / 2000; // Faster 2-second cycle
                    const baseVolume = 0.25 + Math.sin(time) * 0.15; // More variation
                    const slowRandom = Math.sin(time * 0.7) * 0.08; // Slightly more randomness
                    const simulatedVolume = baseVolume + slowRandom;

                    const newHeights = barHeights.map((currentHeight, i) => {
                        // AIDEV-NOTE: Distance from center (0 = center, 1 = edge)
                        const distanceFromCenter = Math.abs(i - 30) / 30;

                        // AIDEV-NOTE: Center bars most sensitive, edges need more volume
                        const threshold = distanceFromCenter * 0.2;
                        const sensitivity = 0.8 - distanceFromCenter * 0.2;

                        // AIDEV-NOTE: Calculate target height based on simulated volume
                        let target = 0.05; // Very low base (almost a line)
                        if (simulatedVolume > threshold) {
                            const volumeAboveThreshold =
                                (simulatedVolume - threshold) * sensitivity;
                            target = 0.05 + volumeAboveThreshold * 1.5;
                            target = Math.max(0.05, Math.min(0.6, target));
                        }

                        // AIDEV-NOTE: Faster, more responsive interpolation
                        const smoothing = 0.25; // Faster response
                        return (
                            currentHeight + (target - currentHeight) * smoothing
                        );
                    });

                    setBarHeights(newHeights);
                } else {
                    // AIDEV-NOTE: Slowly return to baseline when paused
                    const newHeights = barHeights.map((currentHeight) => {
                        const target = 0.05;
                        const smoothing = 0.15;
                        return (
                            currentHeight + (target - currentHeight) * smoothing
                        );
                    });
                    setBarHeights(newHeights);
                }
            }

            // AIDEV-NOTE: Always animate gradient, even when paused
            gradientPositionRef.current += 0.016 / 7; // Moderate gradient movement

            // AIDEV-NOTE: Clear canvas without filling so the parent background shows through
            ctx.clearRect(0, 0, width, height);

            const barCount = barHeights.length;
            const barWidth = width / barCount;

            barHeights.forEach((barHeight, i) => {
                const x = i * barWidth;

                // AIDEV-NOTE: Use raw bar height (can be very small, like a line)
                const heightValue = Math.max(0.03, Math.min(0.95, barHeight));
                const barHeightPixels = height * heightValue;
                const y = (height - barHeightPixels) / 2;

                // AIDEV-NOTE: Get color from moving gradient
                const barPosition = i / barCount;
                const gradientPos =
                    (barPosition + gradientPositionRef.current) % 1;

                // AIDEV-NOTE: Low opacity when paused, high when playing
                const opacity = isPlaying ? 0.85 : 0.2;
                const barColor = getColorFromGradient(gradientPos, opacity);

                // AIDEV-NOTE: Draw rounded bars (or lines when very small)
                ctx.fillStyle = barColor;
                ctx.beginPath();
                const radius = Math.min(barWidth / 2.5, 6);
                ctx.roundRect(
                    x + 1.5,
                    y,
                    barWidth - 3,
                    barHeightPixels,
                    radius,
                );
                ctx.fill();
            });

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener("resize", resizeCanvas);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isPlaying, barHeights]);

    return (
        <div className="w-full">
            {/* AIDEV-NOTE: Histogram Visualization - NOT a time indicator, purely visual */}
            <div className="relative w-full h-32 rounded-xl overflow-hidden">
                <canvas ref={canvasRef} className="w-full h-full" />
            </div>
        </div>
    );
}
